import {
  useEffect, useRef, useState, FC,
} from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { renderAsync } from 'docx-preview';
import DOMPurify from 'dompurify';
import { DICTIONARY } from 'shared/constants';

interface Props {
  uri: string;
}

// Renders a .docx in-browser via docx-preview, fetching the presigned URL
// directly instead of routing it through Microsoft's Office Online viewer.
const DocxViewer: FC<Props> = ({ uri }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let ignore = false;

    // Reset per document: otherwise one failed render leaves the container
    // display:none and every later one is invisible.
    setIsRendering(true);
    setHasError(false);

    const render = async () => {
      try {
        const response = await fetch(uri);

        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }

        const blob = await response.blob();

        if (ignore || !containerRef.current) {
          return;
        }

        await renderAsync(blob, containerRef.current, undefined, {
          className: 'docx',
          inWrapper: true,
          ignoreHeight: false,
          ignoreWidth: false,
          // XSS: docx-preview renders embedded HTML ("altChunk") into an
          // unsandboxed srcdoc iframe, which runs script on our origin. These
          // documents are uploaded by foreign nationals. Do not re-enable.
          renderAltChunks: false,
          // docx-preview never revokes the object URLs it mints for images.
          useBase64URL: true,
        });

        if (ignore || !containerRef.current) {
          return;
        }

        // docx hyperlink targets reach href unchecked, so `javascript:` survives
        // the option above.
        DOMPurify.sanitize(containerRef.current, { IN_PLACE: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);

        if (!ignore) {
          setHasError(true);
        }
      } finally {
        if (!ignore) {
          setIsRendering(false);
        }
      }
    };

    render();

    return () => { ignore = true; };
  }, [uri]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', width: '100%', backgroundColor: (t) => t.palette.action.hover }}>
      {isRendering && (
        <Box sx={{ alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center', width: '100%' }}>
          <CircularProgress />
        </Box>
      )}

      {hasError && !isRendering && (
        <Box sx={{ alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center', width: '100%' }}>
          <Typography>{DICTIONARY.DownloadError}</Typography>
        </Box>
      )}

      <Box ref={containerRef} sx={{ display: hasError ? 'none' : 'block' }} />
    </Box>
  );
};

export default DocxViewer;
